import {useEffect, useState} from 'react'
import {Button, Checkbox, Input, Space} from 'antd'
import {ClearOutlined, CopyOutlined} from '@ant-design/icons'

const SEPARATORS = /[\s,;]+/

function parseTokens(text: string): string[] {
  return text.split(SEPARATORS).map((s) => s.trim()).filter(Boolean)
}

function dedup(arr: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const x of arr) {
    if (!seen.has(x)) {
      seen.add(x)
      out.push(x)
    }
  }
  return out
}

function dedupSorted(arr: string[]): string[] {
  return dedup(arr).sort()
}

export default function CurrencySelector(props: {
  currencies: string[]
  value: string[]
  onChange: (value: string[]) => void
}) {
  const [text, setText] = useState((props.value ?? []).join(', '))
  const allowed = new Set(props.currencies)

  useEffect(() => {
    const current = dedupSorted(parseTokens(text).filter((t) => allowed.has(t)))
    const next = [...(props.value ?? [])].sort()
    if (current.join(',') !== next.join(',')) {
      setText(next.join(', '))
    }
  }, [props.value])

  const selected = new Set(props.value ?? [])
  const tokens = parseTokens(text)
  const unknown = dedup(tokens.filter((t) => !allowed.has(t)))

  const onTextChange = (newText: string) => {
    setText(newText)
    props.onChange(dedupSorted(parseTokens(newText).filter((t) => allowed.has(t))))
  }

  const toggle = (c: string, checked: boolean) => {
    const current = props.value ?? []
    if (checked) props.onChange(dedupSorted([...current, c]))
    else props.onChange(current.filter((x) => x !== c))
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
      <Space.Compact style={{width: '100%'}}>
        <Input
          value={text}
          status={unknown.length ? 'warning' : undefined}
          onChange={(e) => onTextChange(e.target.value.toUpperCase())}
          placeholder="USD, EUR, GBP — paste or type (comma/space/semicolon-separated)"
        />
        <Button
          icon={<CopyOutlined/>}
          title="Copy"
          onClick={() => {
            navigator.clipboard.writeText((props.value ?? []).join(', '))
          }}
        />
        <Button
          danger
          icon={<ClearOutlined/>}
          title="Clear"
          onClick={() => {
            setText('')
            props.onChange([])
          }}
        />
      </Space.Compact>
      {unknown.length > 0 && (
        <span style={{color: '#faad14'}}>unknown: {unknown.join(', ')}</span>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: '4px 12px',
          maxHeight: 300,
          overflowY: 'auto',
          padding: 8,
          border: '1px solid #d9d9d9',
          borderRadius: 6,
        }}
      >
        {props.currencies.map((c) => (
          <Checkbox
            key={c}
            checked={selected.has(c)}
            onChange={(e) => toggle(c, e.target.checked)}
          >
            {c}
          </Checkbox>
        ))}
      </div>
    </div>
  )
}